import { useState } from 'react'
import { Button, Form, Input, Select, Typography } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen } from '@fortawesome/pro-solid-svg-icons'
import { useMerchantApplication } from '../MerchantApplicationContext'
import { getForCountry } from '../../../services/statesService'
import StepFooter from '../components/StepFooter'

const { Title, Text } = Typography

type Props = {
  onNext: () => void
}

const ConfirmInfoStep = ({ onNext }: Props) => {
  const { state, dispatch } = useMerchantApplication()
  const { confirmInfo } = state
  const [isEditing, setIsEditing] = useState(false)
  const [form] = Form.useForm()

  const handleConfirm = () => {
    if (isEditing) {
      form.validateFields().then(values => {
        Object.entries(values).forEach(([field, value]) => {
          dispatch({
            type: 'UPDATE_CONFIRM_INFO',
            field: field as keyof typeof confirmInfo,
            value: value as string,
          })
        })
        setIsEditing(false)
      }).catch(() => {
        // validation errors shown inline
      })
    } else {
      onNext()
    }
  }

  const fullAddress = [confirmInfo.address1, confirmInfo.city, confirmInfo.stateCode, confirmInfo.zip]
    .filter(Boolean)
    .join(', ')

  if (isEditing) {
    return (
      <>
        <div className="merchant-app-step-header">
          <Title level={3}>Edit Your Information</Title>
          <Text type="secondary">Update any fields that need correction.</Text>
        </div>
        <div className="merchant-app-step-body">
          <Form
            form={form}
            layout="vertical"
            initialValues={confirmInfo}
            size="large"
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <Form.Item label="First Name" name="firstName" rules={[{ required: true, message: 'Required' }]}>
                <Input />
              </Form.Item>
              <Form.Item label="Last Name" name="lastName" rules={[{ required: true, message: 'Required' }]}>
                <Input />
              </Form.Item>
            </div>
            <Form.Item label="Email" name="email" rules={[{ required: true, type: 'email', message: 'Valid email required' }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Business Legal Name" name="businessName" rules={[{ required: true, message: 'Required' }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Business DBA (if different)" name="businessDba">
              <Input placeholder="Doing business as..." />
            </Form.Item>
            <Form.Item label="Phone Number" name="phoneNumber" rules={[{ required: true, message: 'Required' }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Website" name="website">
              <Input placeholder="www.example.com" />
            </Form.Item>
            <Form.Item label="Street Address" name="address1" rules={[{ required: true, message: 'Required' }]}>
              <Input />
            </Form.Item>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0 16px' }}>
              <Form.Item label="City" name="city" rules={[{ required: true, message: 'Required' }]}>
                <Input />
              </Form.Item>
              <Form.Item label="State" name="stateCode" rules={[{ required: true, message: 'Required' }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={getForCountry('us').map(s => ({ value: s.code, label: s.code }))}
                />
              </Form.Item>
              <Form.Item label="ZIP" name="zip" rules={[{ required: true, message: 'Required' }]}>
                <Input />
              </Form.Item>
            </div>
          </Form>
        </div>
        <div className="merchant-app-step-footer">
          <Button size="large" onClick={() => setIsEditing(false)}>
            Cancel
          </Button>
          <Button type="primary" size="large" onClick={handleConfirm}>
            Save Changes
          </Button>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="merchant-app-step-header">
        <Title level={3}>Confirm Your Information</Title>
        <Text type="secondary">
          We pulled this from your SynkedUP account. If everything looks right, just hit confirm.
        </Text>
      </div>
      <div className="merchant-app-step-body">
        <div className="merchant-app-confirm-card">
          <div className="merchant-app-confirm-row">
            <div className="merchant-app-confirm-label">Name</div>
            <div className="merchant-app-confirm-value">{confirmInfo.firstName} {confirmInfo.lastName}</div>
          </div>
          <div className="merchant-app-confirm-row">
            <div className="merchant-app-confirm-label">Email</div>
            <div className="merchant-app-confirm-value">{confirmInfo.email}</div>
          </div>
          <div className="merchant-app-confirm-row">
            <div className="merchant-app-confirm-label">Business Name</div>
            <div className="merchant-app-confirm-value">{confirmInfo.businessName}</div>
          </div>
          {confirmInfo.businessDba && (
            <div className="merchant-app-confirm-row">
              <div className="merchant-app-confirm-label">DBA</div>
              <div className="merchant-app-confirm-value">{confirmInfo.businessDba}</div>
            </div>
          )}
          <div className="merchant-app-confirm-row">
            <div className="merchant-app-confirm-label">Phone</div>
            <div className="merchant-app-confirm-value">{confirmInfo.phoneNumber}</div>
          </div>
          {confirmInfo.website && (
            <div className="merchant-app-confirm-row">
              <div className="merchant-app-confirm-label">Website</div>
              <div className="merchant-app-confirm-value">{confirmInfo.website}</div>
            </div>
          )}
          <div className="merchant-app-confirm-row">
            <div className="merchant-app-confirm-label">Business Address</div>
            <div className="merchant-app-confirm-value">{fullAddress}</div>
          </div>
        </div>

        <Button
          type="link"
          style={{ padding: 0, color: '#FF5C35' }}
          onClick={() => setIsEditing(true)}
        >
          <FontAwesomeIcon icon={faPen} style={{ marginRight: 6 }} />
          Something not right? Edit your information
        </Button>
      </div>
      <StepFooter onNext={onNext} nextLabel="Looks Good, Continue" />
    </>
  )
}

export default ConfirmInfoStep
